import { useState, useEffect } from 'react'
import { brandsApi, type BrandData } from '../api/client'

interface UseBrandfetchResult {
  brand: BrandData | null
  isLoading: boolean
  /** logoUrl resolves to Brandfetch icon URL, or falls back to the Google Places profile photo */
  logoUrl: string | null
  /** Primary brand color from Brandfetch, or null if unavailable */
  primaryColor: string | null
}

/**
 * useBrandfetch fetches brand data for the given website URL via the
 * backend Brandfetch proxy (which securely holds the server-side API key).
 *
 * It returns the brand's logo URL and primary color, degrading gracefully
 * to null values if the domain is missing or the API call fails.
 *
 * @param websiteUrl - The full URL (e.g. "https://dostacos.com") or just the domain
 * @param fallbackLogoUrl - Optional fallback logo (e.g. Google Places profile photo)
 */
export function useBrandfetch(
  websiteUrl: string | null | undefined,
  fallbackLogoUrl?: string | null,
): UseBrandfetchResult {
  const [brand, setBrand] = useState<BrandData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!websiteUrl) {
      setBrand(null)
      return
    }

    setIsLoading(true)
    brandsApi.getBrand(websiteUrl)
      .then(data => setBrand(data))
      .catch(() => setBrand(null))
      .finally(() => setIsLoading(false))
  }, [websiteUrl])

  // Resolve the best available logo:
  // 1. Brandfetch icon URL (small standalone mark — best for avatar/header use)
  // 2. Brandfetch logo URL (full wordmark)
  // 3. Fallback (Google Places profile photo)
  const logoUrl = brand?.icon_url || brand?.logo_url || fallbackLogoUrl || null

  return {
    brand,
    isLoading,
    logoUrl,
    primaryColor: brand?.primary_color || null,
  }
}
